import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const PensionFund = () => {
  // State variables for Pension Fund calculation
  const [monthlyContribution, setMonthlyContribution] = useState(0); // Monthly contribution
  const [interestRate, setInterestRate] = useState(0); // Interest rate (annual rate)
  const [years, setYears] = useState(0); // Number of years
  const [pensionFund, setPensionFund] = useState(0); // Calculated pension fund amount

  // Event handlers to update state values
  const handleContributionChange = (e) =>
    setMonthlyContribution(parseFloat(e.target.value) || 0);
  const handleInterestRateChange = (e) =>
    setInterestRate(parseFloat(e.target.value) || 0);
  const handleYearsChange = (e) => setYears(parseInt(e.target.value) || 0);

  // useEffect hook to calculate the Pension Fund
  useEffect(() => {
    if (monthlyContribution > 0 && interestRate > 0 && years > 0) {
      // Formula: Pension Fund = Monthly Contribution x [(1 + Interest Rate)^Years - 1] / Interest Rate
      const rate = interestRate / 100; // Convert percentage to decimal
      const pensionAmount =
        monthlyContribution * ((Math.pow(1 + rate, years) - 1) / rate);
      setPensionFund(pensionAmount);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [monthlyContribution, interestRate, years]);

  return (
    <Layout title="Pension Fund Calculator" path="pension-fund">
      <section className="section-padding" id="pension-fund">
        <div className="container mt-5">
          <div className="row">
            {/* Pension Fund Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Pension Fund Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Input for Monthly Contribution */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="monthlyContribution"
                        className="form-label"
                      >
                        Monthly Contribution
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Monthly Contribution:</strong> The amount of money you contribute to your pension fund every month."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyContribution"
                        placeholder="Enter monthly contribution"
                        value={monthlyContribution || ""}
                        onChange={handleContributionChange}
                      />
                    </div>

                    {/* Input for Interest Rate */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="interestRate" className="form-label">
                        Interest Rate (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Interest Rate:</strong> The annual interest rate expected on your pension fund investment, expressed as a percentage."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="interestRate"
                        placeholder="Enter annual interest rate"
                        value={interestRate || ""}
                        onChange={handleInterestRateChange}
                      />
                    </div>

                    {/* Input for Number of Years */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="years" className="form-label">
                        Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Years:</strong> The number of years you plan to contribute to your pension fund."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="years"
                        placeholder="Enter number of years"
                        value={years || ""}
                        onChange={handleYearsChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Estimated Pension Fund:{" "}
                        <span className="fw-bold ms-3">
                          {pensionFund === 0
                            ? "0"
                            : pensionFund.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                          {" currency units"}
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Pension Fund
                </h3>

                {/* What is a Pension Fund? */}
                <h4 className="mt-3 mb-3">What is a Pension Fund?</h4>
                <p>
                  A **Pension Fund** is a financial vehicle used to provide
                  income to an individual during retirement. It is built over
                  time through regular contributions and the accumulation of
                  interest on those contributions.
                </p>

                {/* Why Use the Pension Fund Calculator? */}
                <h4 className="mt-4 mb-3">
                  Why Use the Pension Fund Calculator?
                </h4>
                <p>
                  This calculator helps individuals estimate how much they need
                  to save for retirement. By considering monthly contributions,
                  interest rates, and years of saving, it provides a realistic
                  target for building financial security for the future.
                </p>

                {/* Benefits of Using the Pension Fund Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using the Pension Fund Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Helps in Retirement Planning:</strong> Allows
                    individuals to determine how much they need to contribute to
                    meet their retirement goals.
                  </li>
                  <li>
                    <strong>
                      Assists in Setting Realistic Saving Targets:
                    </strong>{" "}
                    Provides insights into how much savings will accumulate over
                    time based on contribution rates and interest rates.
                  </li>
                  <li>
                    <strong>Supports Financial Security for Retirement:</strong>{" "}
                    Helps ensure that individuals can maintain their desired
                    lifestyle post-retirement.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">
                  Who Should Use the Pension Fund Calculator?
                </h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Individuals:</strong> Ideal for those who are
                    planning for retirement and want to assess their savings
                    potential.
                  </li>
                  <li>
                    <strong>Retirement Planners:</strong> Useful for
                    professionals helping clients plan for a secure financial
                    future.
                  </li>
                  <li>
                    <strong>Financial Advisors:</strong> Assists financial
                    advisors in guiding clients towards proper retirement
                    savings strategies.
                  </li>
                </ul>

                {/* Two Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How can I adjust my contributions to meet my retirement goals?
                </h4>
                <p>
                  Adjusting contributions can involve increasing monthly savings
                  or extending the number of years for contributions. The
                  calculator helps you model different scenarios.
                </p>

                <h4 className="mt-4 mb-3">
                  How does the interest rate affect my pension fund?
                </h4>
                <p>
                  The interest rate plays a significant role in growing your
                  pension fund over time. Even small changes in the interest
                  rate can significantly impact the final amount at retirement.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PensionFund;
